if (!customElements.get('results-media-carousel')) {
  class ResultsMediaCarousel extends HTMLElement {
    constructor() {
      super();
      this.onScroll = this.onScroll.bind(this);
      this.onResize = this.debounce(this.rebuild.bind(this), 150);
      this.onVisibilityChange = this.onVisibilityChange.bind(this);
      this.onMotionChange = this.onMotionChange.bind(this);
      this.onSectionIntersection = this.onSectionIntersection.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
    }

    connectedCallback() {
      this.viewport = this.querySelector('[data-results-viewport]');
      this.track = this.querySelector('[data-results-track]');
      this.previousButton = this.querySelector('[data-results-previous]');
      this.nextButton = this.querySelector('[data-results-next]');
      this.dots = Array.from(this.querySelectorAll('[data-results-dot]'));
      this.originalSlides = Array.from(this.querySelectorAll('[data-result-slide]'));
      this.mediaCount = this.originalSlides.length;

      if (!this.viewport || !this.track || !this.mediaCount) return;

      this.autoplayEnabled = this.dataset.autoplay === 'true' && this.mediaCount > 1;
      this.autoplayDelay = Number(this.dataset.autoplayDelay) || 2500;
      this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.sectionIsVisible = true;

      this.previousButton?.addEventListener('click', () => this.moveBy(-1));
      this.nextButton?.addEventListener('click', () => this.moveBy(1));
      this.dots.forEach((dot) => dot.addEventListener('click', () => this.goTo(Number(dot.dataset.resultsDot))));
      this.viewport.addEventListener('scroll', this.onScroll, { passive: true });
      this.viewport.addEventListener('keydown', this.onKeydown);
      this.addEventListener('mouseenter', () => this.stopAutoplay());
      this.addEventListener('mouseleave', () => this.startAutoplay());
      this.addEventListener('focusin', () => this.stopAutoplay());
      this.addEventListener('focusout', (event) => {
        if (!this.contains(event.relatedTarget)) this.startAutoplay();
      });
      window.addEventListener('resize', this.onResize);
      document.addEventListener('visibilitychange', this.onVisibilityChange);
      this.motionQuery.addEventListener('change', this.onMotionChange);

      this.intersectionObserver = new IntersectionObserver(this.onSectionIntersection, { threshold: 0.15 });
      this.intersectionObserver.observe(this);

      this.rebuild();
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this.onResize);
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
      this.motionQuery?.removeEventListener('change', this.onMotionChange);
      this.intersectionObserver?.disconnect();
      this.stopAutoplay();
      window.clearTimeout(this.scrollEndTimer);
    }

    rebuild() {
      if (!this.track || !this.mediaCount) return;

      this.stopAutoplay();
      this.track.querySelectorAll('[data-results-clone]').forEach((clone) => clone.remove());

      if (this.mediaCount > 1) {
        const before = this.originalSlides.map((slide) => this.makeClone(slide));
        const after = this.originalSlides.map((slide) => this.makeClone(slide));
        before.reverse().forEach((clone) => this.track.prepend(clone));
        after.forEach((clone) => this.track.append(clone));
      }

      requestAnimationFrame(() => {
        this.step = this.getStep();
        this.jumpToRawIndex(this.mediaCount > 1 ? this.mediaCount : 0);
        this.updateActiveState();
        this.updateVideos();
        this.startAutoplay();
      });
    }

    makeClone(slide) {
      const clone = slide.cloneNode(true);
      clone.removeAttribute('id');
      clone.removeAttribute('role');
      clone.removeAttribute('aria-label');
      clone.removeAttribute('aria-roledescription');
      clone.removeAttribute('data-result-slide');
      clone.removeAttribute('data-shopify-editor-block');
      clone.dataset.resultsClone = 'true';
      clone.setAttribute('aria-hidden', 'true');
      clone.querySelectorAll('[id]').forEach((element) => element.removeAttribute('id'));
      return clone;
    }

    getStep() {
      const slides = this.track.children;
      if (slides.length < 2) return this.viewport.clientWidth;
      return slides[1].offsetLeft - slides[0].offsetLeft;
    }

    getRawIndex() {
      return Math.round(this.viewport.scrollLeft / (this.step || 1));
    }

    getLogicalIndex(rawIndex = this.getRawIndex()) {
      if (this.mediaCount <= 1) return 0;
      return ((rawIndex - this.mediaCount) % this.mediaCount + this.mediaCount) % this.mediaCount;
    }

    moveBy(amount) {
      this.stopAutoplay();
      this.scrollToRawIndex(this.getRawIndex() + amount);
      this.restartAutoplaySoon();
    }

    goTo(index) {
      this.stopAutoplay();
      this.scrollToRawIndex(this.mediaCount + index);
      this.restartAutoplaySoon();
    }

    scrollToRawIndex(index) {
      this.viewport.scrollTo({ left: index * this.step, behavior: this.motionQuery.matches ? 'auto' : 'smooth' });
    }

    jumpToRawIndex(index) {
      const previousBehavior = this.viewport.style.scrollBehavior;
      this.viewport.style.scrollBehavior = 'auto';
      this.viewport.scrollLeft = index * this.step;
      this.viewport.style.scrollBehavior = previousBehavior;
    }

    onScroll() {
      this.updateActiveState();
      this.updateVideos();
      window.clearTimeout(this.scrollEndTimer);
      this.scrollEndTimer = window.setTimeout(() => this.normalizePosition(), 100);
    }

    normalizePosition() {
      if (this.mediaCount <= 1) return;
      const rawIndex = this.getRawIndex();
      if (rawIndex < this.mediaCount || rawIndex >= this.mediaCount * 2) {
        this.jumpToRawIndex(this.mediaCount + this.getLogicalIndex(rawIndex));
        this.updateVideos();
      }
    }

    updateActiveState() {
      const activeIndex = this.getLogicalIndex();
      this.dots.forEach((dot, index) => {
        const active = index === activeIndex;
        dot.classList.toggle('is-active', active);
        if (active) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
    }

    updateVideos() {
      const canPlay = !this.motionQuery.matches && this.sectionIsVisible && !document.hidden;
      this.querySelectorAll('video').forEach((video) => {
        const rect = video.getBoundingClientRect();
        const viewportRect = this.viewport.getBoundingClientRect();
        const isVisible = rect.right > viewportRect.left && rect.left < viewportRect.right;
        if (canPlay && isVisible) video.play().catch(() => {});
        else video.pause();
      });
    }

    startAutoplay() {
      if (!this.autoplayEnabled || this.motionQuery.matches || !this.sectionIsVisible || document.hidden) return;
      this.stopAutoplay();
      this.autoplayTimer = window.setInterval(() => this.scrollToRawIndex(this.getRawIndex() + 1), this.autoplayDelay);
    }

    stopAutoplay() {
      window.clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }

    restartAutoplaySoon() {
      window.clearTimeout(this.autoplayRestartTimer);
      this.autoplayRestartTimer = window.setTimeout(() => this.startAutoplay(), this.autoplayDelay);
    }

    onKeydown(event) {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.moveBy(-1);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.moveBy(1);
      }
    }

    onVisibilityChange() {
      if (document.hidden) this.stopAutoplay();
      else this.startAutoplay();
      this.updateVideos();
    }

    onMotionChange() {
      if (this.motionQuery.matches) this.stopAutoplay();
      else this.startAutoplay();
      this.updateVideos();
    }

    onSectionIntersection(entries) {
      this.sectionIsVisible = entries[0]?.isIntersecting ?? true;
      if (this.sectionIsVisible) this.startAutoplay();
      else this.stopAutoplay();
      this.updateVideos();
    }

    debounce(callback, wait) {
      let timeout;
      return (...args) => {
        window.clearTimeout(timeout);
        timeout = window.setTimeout(() => callback(...args), wait);
      };
    }
  }

  customElements.define('results-media-carousel', ResultsMediaCarousel);
}
